import {
  XlsxColumn,
  XlsxSheet,
  XlsxReportMeta,
  XlsxTableSection,
  XlsxColumnType,
  XlsxColumnDef,
  XlsxTypedCell,
} from "@/interfaces";
import { format } from "date-fns";

export type { XlsxReportMeta, XlsxTableSection, XlsxColumnType, XlsxColumnDef, XlsxTypedCell };

export type XlsxCellValue = string | number | boolean | null | undefined | Date | XlsxTypedCell;

function unwrapCell(value: XlsxCellValue): { value: Exclude<XlsxCellValue, XlsxTypedCell>; type?: XlsxColumnType } {
  if (value != null && typeof value === "object" && !(value instanceof Date) && "value" in value) {
    return { value: value.value, type: value.type };
  }
  return { value: value as Exclude<XlsxCellValue, XlsxTypedCell> };
}

const COLORS = {
  brand: "3DAEF5",
  title: "0A173E",
  muted: "475569",
  headerBg: "0A173E",
  headerFg: "FFFFFF",
  sectionTitleBg: "0A173E",
  sectionTitleFg: "FFFFFF",
  columnHeaderBg: "E2E8F0",
  columnHeaderFg: "0F172A",
  primaryMetricBg: "E0F2FE",
  primaryMetricFg: "0C4A6E",
  rowAlt: "F9FAFB",
  row: "FFFFFF",
  positive: "059669",
  negative: "E11D48",
  zero: "64748B",
  border: "94A3B8",
} as const;

const NUMBER_FORMAT: Record<Exclude<XlsxColumnType, "string">, string> = {
  integer: "#,##0",
  decimal: "0.0",
  percent: "0%",
  delta: "+#,##0;-#,##0;0",
  deltaDecimal: "+0.0;-0.0;0",
  deltaPercentPoints: '+0" pts";-0" pts";0" pts"',
};

type CellStyle = {
  font?: {
    bold?: boolean;
    sz?: number;
    color?: { rgb: string };
  };
  fill?: {
    patternType: "solid";
    fgColor: { rgb: string };
  };
  alignment?: {
    horizontal?: "left" | "center" | "right";
    vertical?: "top" | "center" | "bottom";
    wrapText?: boolean;
  };
  border?: CellBorder;
};

type BorderSide = { style: "thin" | "medium"; color: { rgb: string } };

type CellBorder = {
  top?: BorderSide;
  bottom?: BorderSide;
  left?: BorderSide;
  right?: BorderSide;
};

function deltaFontColor(value: number): string {
  if (value > 0) return COLORS.positive;
  if (value < 0) return COLORS.negative;
  return COLORS.zero;
}

function isDeltaType(type?: XlsxColumnType): boolean {
  return type === "delta" || type === "deltaDecimal" || type === "deltaPercentPoints";
}

type MetaRow =
  | { variant: "company" | "title" | "muted"; value: string }
  | { variant: "detail"; label: string; value: string };

const TOP_ALIGNED = { vertical: "top" } as const;

const META_ROW_STYLE: Record<"company" | "title" | "muted" | "detailLabel" | "detailValue", CellStyle> = {
  company: { font: { bold: true, sz: 18, color: { rgb: COLORS.title } }, alignment: TOP_ALIGNED },
  title: { font: { bold: true, sz: 13, color: { rgb: COLORS.brand } }, alignment: TOP_ALIGNED },
  muted: { font: { sz: 11, color: { rgb: COLORS.muted } }, alignment: TOP_ALIGNED },
  detailLabel: { font: { bold: true, sz: 11, color: { rgb: COLORS.muted } }, alignment: TOP_ALIGNED },
  detailValue: { font: { sz: 11, color: { rgb: COLORS.title } }, alignment: TOP_ALIGNED },
};

function buildMetaRows(meta: XlsxReportMeta): MetaRow[] {
  const generatedAt = meta.generatedAt ?? new Date();
  const rows: MetaRow[] = [];
  if (meta.companyName) rows.push({ variant: "company", value: meta.companyName });
  rows.push({ variant: "title", value: meta.reportName });
  if (meta.description) rows.push({ variant: "muted", value: meta.description });
  rows.push({ variant: "detail", label: "Generated", value: format(generatedAt, "yyyy-MM-dd HH:mm") });
  const filters = (meta.filters ?? []).filter(Boolean);
  if (filters.length) rows.push({ variant: "detail", label: "Filters", value: filters.join(" · ") });
  if (meta.totalRecords != null) {
    rows.push({ variant: "detail", label: "Total Records", value: String(meta.totalRecords) });
  }
  return rows;
}

function resolveAlign(column: XlsxColumnDef): "left" | "center" | "right" {
  if (column.align) return column.align;
  return column.type && column.type !== "string" ? "right" : "left";
}

function writeCell(
  ws: Record<string, any>,
  row: number,
  col: number,
  rawValue: XlsxCellValue,
  opts: { type?: XlsxColumnType; style?: CellStyle } = {}
) {
  const unwrapped = unwrapCell(rawValue);
  const value = unwrapped.value;
  const type = unwrapped.type ?? opts.type;

  if (value == null || value === "") {
    ws[encodeCell(row, col)] = { t: "s", v: "", s: opts.style };
    return;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const resolvedType = type && type !== "string" ? type : "integer";
    const style: CellStyle = { ...(opts.style ?? {}) };
    if (isDeltaType(resolvedType)) {
      style.font = {
        ...(style.font ?? {}),
        color: { rgb: deltaFontColor(value) },
        bold: true,
      };
    }
    ws[encodeCell(row, col)] = {
      t: "n",
      v: value,
      z: NUMBER_FORMAT[resolvedType as Exclude<XlsxColumnType, "string">] ?? NUMBER_FORMAT.integer,
      s: style,
    };
    return;
  }

  if (value instanceof Date) {
    ws[encodeCell(row, col)] = {
      t: "d",
      v: value,
      z: "yyyy-mm-dd",
      s: opts.style,
    };
    return;
  }

  ws[encodeCell(row, col)] = {
    t: "s",
    v: String(value),
    s: opts.style,
  };
}

function encodeCell(row: number, col: number): string {
  // local copy to avoid depending on XLSX before dynamic import for helpers used in CSV too
  let c = col;
  let colName = "";
  while (c >= 0) {
    colName = String.fromCharCode((c % 26) + 65) + colName;
    c = Math.floor(c / 26) - 1;
  }
  return `${colName}${row + 1}`;
}

function ensureSheetRange(ws: Record<string, any>, endRow: number, endCol: number) {
  ws["!ref"] = `A1:${encodeCell(endRow, endCol)}`;
}

/** Sheet-column offset of each column plus the total sheet columns a section occupies. */
function sectionLayout(section: XlsxTableSection): { offsets: number[]; width: number } {
  const offsets: number[] = [];
  let width = 0;
  section.columns.forEach(column => {
    offsets.push(width);
    width += Math.max(1, column.span ?? 1);
  });
  return { offsets, width: Math.max(width, 1) };
}

function autoWidthsFromSections(sections: XlsxTableSection[]): XlsxColumn[] {
  const maxCols = Math.max(1, ...sections.map(section => sectionLayout(section).width));
  const widths: number[] = Array.from({ length: maxCols }, () => 12);

  sections.forEach(section => {
    const { offsets } = sectionLayout(section);
    widths[0] = Math.max(widths[0] ?? 12, section.title.length + 2);
    section.columns.forEach((column, index) => {
      // Spanned columns borrow their width from the columns they merge across.
      if ((column.span ?? 1) > 1) return;
      const sheetCol = offsets[index];
      let max = column.header.length;
      section.rows.forEach(row => {
        const value = unwrapCell(row[column.key]).value;
        max = Math.max(max, value == null ? 0 : String(value).length);
      });
      widths[sheetCol] = Math.max(widths[sheetCol] ?? 12, Math.min(max + 2, column.wch ?? 50));
    });
  });

  return widths.map(wch => ({ wch: Math.min(wch, 50) }));
}

/** Merged wrapped text does not auto-fit in Excel, so approximate the height it needs. */
function mergedTextHeight(text: string, availableChars: number): number | null {
  const charsPerLine = Math.max(availableChars, 10);
  const lines = Math.ceil(text.length / charsPerLine);
  if (lines <= 1) return null;
  return Math.min(lines, 12) * 14.5;
}

/** Outlines a rectangular block of already-written cells (section title through last data row). */
function applyBlockBorder(
  ws: Record<string, any>,
  bounds: { startRow: number; endRow: number; startCol: number; endCol: number }
) {
  const side: BorderSide = { style: "thin", color: { rgb: COLORS.border } };

  for (let row = bounds.startRow; row <= bounds.endRow; row += 1) {
    for (let col = bounds.startCol; col <= bounds.endCol; col += 1) {
      const cell = ws[encodeCell(row, col)];
      if (!cell) continue;
      const border: CellBorder = { ...(cell.s?.border ?? {}) };
      if (row === bounds.startRow) border.top = side;
      if (row === bounds.endRow) border.bottom = side;
      if (col === bounds.startCol) border.left = side;
      if (col === bounds.endCol) border.right = side;
      cell.s = { ...(cell.s ?? {}), border };
    }
  }
}

function buildSectionedWorksheet(XLSX: any, sheet: XlsxSheet & { meta: XlsxReportMeta; sections: XlsxTableSection[] }) {
  const ws: Record<string, any> = {};
  const metaRows = buildMetaRows(sheet.meta);
  const contentWidths = sheet.columns ?? autoWidthsFromSections(sheet.sections);
  const contentStartCol = 1;
  const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];
  const rowHeights: ({ hpt: number } | undefined)[] = [];
  let row = 0;
  let maxCol = contentStartCol;

  /** Writes a value at a column's offset, filling and merging the columns it spans. */
  const writeSpannedCell = (
    targetRow: number,
    offset: number,
    span: number,
    value: XlsxCellValue,
    opts: { type?: XlsxColumnType; style?: CellStyle }
  ) => {
    writeCell(ws, targetRow, contentStartCol + offset, value, opts);
    for (let extra = 1; extra < span; extra += 1) {
      writeCell(ws, targetRow, contentStartCol + offset + extra, "", { style: opts.style });
    }
    if (span > 1) {
      merges.push({
        s: { r: targetRow, c: contentStartCol + offset },
        e: { r: targetRow, c: contentStartCol + offset + span - 1 },
      });
    }
  };

  metaRows.forEach(metaRow => {
    if (metaRow.variant === "detail") {
      writeCell(ws, row, contentStartCol, metaRow.label, { style: META_ROW_STYLE.detailLabel });
      writeCell(ws, row, contentStartCol + 1, metaRow.value, { style: META_ROW_STYLE.detailValue });
      maxCol = Math.max(maxCol, contentStartCol + 1);
    } else {
      writeCell(ws, row, contentStartCol, metaRow.value, { style: META_ROW_STYLE[metaRow.variant] });
    }
    row += 1;
  });

  sheet.sections.forEach(section => {
    row += 1; // blank spacer
    const { offsets, width } = sectionLayout(section);
    const sectionStartRow = row;

    writeCell(ws, row, contentStartCol, section.title, {
      style: {
        font: { bold: true, sz: 12, color: { rgb: COLORS.sectionTitleFg } },
        fill: { patternType: "solid", fgColor: { rgb: COLORS.sectionTitleBg } },
        alignment: TOP_ALIGNED,
      },
    });
    for (let col = 1; col < width; col += 1) {
      writeCell(ws, row, col + contentStartCol, "", {
        style: {
          fill: { patternType: "solid", fgColor: { rgb: COLORS.sectionTitleBg } },
          alignment: TOP_ALIGNED,
        },
      });
    }
    maxCol = Math.max(maxCol, contentStartCol + width - 1);
    row += 1;

    section.columns.forEach((column, index) => {
      writeSpannedCell(row, offsets[index], Math.max(1, column.span ?? 1), column.header, {
        style: {
          font: { bold: true, color: { rgb: COLORS.columnHeaderFg } },
          fill: { patternType: "solid", fgColor: { rgb: COLORS.columnHeaderBg } },
          alignment: { horizontal: resolveAlign(column), vertical: "top", wrapText: true },
        },
      });
    });
    row += 1;

    section.rows.forEach((dataRow, rowIndex) => {
      const isPrimaryMetric = section.emphasizeFirstRow === true && rowIndex === 0;
      const bg = isPrimaryMetric ? COLORS.primaryMetricBg : rowIndex % 2 === 0 ? COLORS.row : COLORS.rowAlt;
      let rowHeight: number | null = null;

      section.columns.forEach((column, index) => {
        const span = Math.max(1, column.span ?? 1);
        const wrapText = span > 1 || (column.wch != null && column.wch >= 50);

        writeSpannedCell(row, offsets[index], span, dataRow[column.key], {
          type: column.type,
          style: {
            font: isPrimaryMetric ? { bold: true, color: { rgb: COLORS.primaryMetricFg } } : undefined,
            fill: { patternType: "solid", fgColor: { rgb: bg } },
            alignment: { horizontal: resolveAlign(column), vertical: "top", wrapText },
          },
        });

        if (span > 1) {
          const value = unwrapCell(dataRow[column.key]).value;
          const availableChars = contentWidths
            .slice(offsets[index], offsets[index] + span)
            .reduce((total, col) => total + (col.wch ?? 12), 0);
          const height = value == null ? null : mergedTextHeight(String(value), availableChars);
          if (height != null) rowHeight = Math.max(rowHeight ?? 0, height);
        }
      });

      if (rowHeight != null) rowHeights[row] = { hpt: rowHeight };
      maxCol = Math.max(maxCol, contentStartCol + width - 1);
      row += 1;
    });

    applyBlockBorder(ws, {
      startRow: sectionStartRow,
      endRow: row - 1,
      startCol: contentStartCol,
      endCol: contentStartCol + width - 1,
    });
  });

  ensureSheetRange(ws, Math.max(row - 1, 0), maxCol);
  ws["!cols"] = [{ wch: 2 }, ...contentWidths];
  if (merges.length) ws["!merges"] = merges;
  if (rowHeights.length) ws["!rows"] = Array.from({ length: row }, (_, index) => rowHeights[index] ?? {});
  return ws;
}

function applyTypedFormatsToJsonSheet(
  XLSX: any,
  ws: Record<string, any>,
  data: Record<string, any>[],
  headerRowIndex: number,
  columnTypes?: Record<string, XlsxColumnType>
) {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  data.forEach((row, rowOffset) => {
    keys.forEach((key, col) => {
      const type = columnTypes?.[key];
      const addr = XLSX.utils.encode_cell({ r: headerRowIndex + 1 + rowOffset, c: col });
      const cell = ws[addr];
      if (!cell) return;

      const value = row[key];
      if (typeof value === "number" && Number.isFinite(value)) {
        cell.t = "n";
        cell.v = value;
        if (type && type !== "string") {
          cell.z = NUMBER_FORMAT[type];
          if (isDeltaType(type)) {
            cell.s = {
              ...(cell.s ?? {}),
              font: {
                ...(cell.s?.font ?? {}),
                color: { rgb: deltaFontColor(value) },
                bold: true,
              },
            };
          }
        } else if (Number.isInteger(value)) {
          cell.z = NUMBER_FORMAT.integer;
        } else {
          cell.z = NUMBER_FORMAT.decimal;
        }
      }
    });
  });
}

/**
 * Exports data to an XLSX file with multiple tabs (sheets).
 * Uses dynamic import to keep the initial bundle size small.
 *
 * Prefer `meta` + `sections` for styled reputation reports.
 * Legacy `data` + `headerRows` remains supported.
 */
export const exportToXlsx = async (filename: string, sheets: XlsxSheet[]) => {
  // Dynamically load xlsx-js-style
  // @ts-ignore
  const XLSX = await import("xlsx-js-style");

  const wb = XLSX.utils.book_new();

  sheets.forEach(sheet => {
    let ws: any;

    if (sheet.meta && sheet.sections?.length) {
      ws = buildSectionedWorksheet(XLSX, sheet as XlsxSheet & { meta: XlsxReportMeta; sections: XlsxTableSection[] });
    } else if (sheet.headerRows && sheet.headerRows.length > 0) {
      ws = XLSX.utils.aoa_to_sheet(sheet.headerRows);

      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const addr = XLSX.utils.encode_cell({ r: R, c: 0 });
        if (!ws[addr]) continue;

        ws[addr].s = {
          font: {
            bold: R === 0 || R === 1,
            color: { rgb: R === 0 ? COLORS.brand : COLORS.muted },
            sz: R === 0 ? 14 : 11,
          },
        };
      }

      if (sheet.data?.length) {
        XLSX.utils.sheet_add_json(ws, sheet.data, {
          origin: `A${sheet.headerRows.length + 2}`,
        });

        const tableHeaderRow = sheet.headerRows.length + 1;
        const dataKeys = Object.keys(sheet.data[0] || {});
        dataKeys.forEach((_, C) => {
          const addr = XLSX.utils.encode_cell({ r: tableHeaderRow, c: C });
          if (ws[addr]) {
            ws[addr].s = {
              font: { bold: true, color: { rgb: COLORS.headerFg } },
              fill: { patternType: "solid", fgColor: { rgb: COLORS.headerBg } },
              alignment: { horizontal: "center" },
            };
          }
        });

        applyTypedFormatsToJsonSheet(XLSX, ws, sheet.data, tableHeaderRow, sheet.columnTypes);
      }
    } else {
      ws = XLSX.utils.json_to_sheet(sheet.data ?? []);
      if (sheet.data?.length) {
        applyTypedFormatsToJsonSheet(XLSX, ws, sheet.data, 0, sheet.columnTypes);
      }
    }

    if (sheet.columns) {
      ws["!cols"] = sheet.columns;
    }

    XLSX.utils.book_append_sheet(wb, ws, sheet.name);
  });

  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const getAutoWidths = (data: any[]) => {
  if (!data.length) return [];
  const MAX_COLUMN_WIDTH = 50;
  const keys = Object.keys(data[0]);
  return keys.map(key => {
    const maxWidth = Math.max(
      key.length,
      ...data.map(row => {
        const val = row[key];
        if (val instanceof Date) return 20;
        return val?.toString().length || 0;
      })
    );

    return {
      wch: Math.min(maxWidth + 2, MAX_COLUMN_WIDTH),
    };
  });
};

function formatCsvNumber(value: number, type?: XlsxColumnType): string {
  switch (type) {
    case "percent":
      return `${Number((value * 100).toFixed(1))}%`;
    case "deltaPercentPoints":
      return `${value > 0 ? "+" : ""}${value} pts`;
    case "delta":
      return `${value > 0 ? "+" : ""}${value}`;
    case "deltaDecimal":
      return `${value > 0 ? "+" : ""}${value.toFixed(1)}`;
    case "decimal":
      return value.toFixed(1);
    case "integer":
      return String(Math.round(value));
    default:
      return String(value);
  }
}

/** CSV export with labeled sections (blank row + section title between tables). */
export function exportSectionedDataToCsv(filename: string, meta: XlsxReportMeta, sections: XlsxTableSection[]) {
  const lines: string[] = [];
  const escape = (rawValue: XlsxCellValue, columnType?: XlsxColumnType) => {
    const unwrapped = unwrapCell(rawValue);
    const value = unwrapped.value;
    const type = unwrapped.type ?? columnType;
    if (value == null || value === "") return "";
    const raw =
      typeof value === "number" && Number.isFinite(value)
        ? formatCsvNumber(value, type)
        : value instanceof Date
        ? format(value, "yyyy-MM-dd")
        : String(value);
    if (/[",\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
    return raw;
  };

  buildMetaRows(meta).forEach(row => {
    if (row.variant === "detail") {
      lines.push([escape(row.label), escape(row.value)].join(","));
    } else {
      lines.push(escape(row.value));
    }
  });
  lines.push("");

  sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) lines.push("");
    lines.push(escape(section.title));
    lines.push(section.columns.map(column => escape(column.header)).join(","));
    section.rows.forEach(row => {
      lines.push(section.columns.map(column => escape(row[column.key], column.type)).join(","));
    });
  });

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
